/**
 * Option 1 heuristic: client-side time-gap annotation and staggered-sequence
 * grouping, computed purely from local receive timestamps. No server change,
 * no correlation ID — this is inference, not ground truth. See the caveats
 * surfaced in the UI (gate-events.component.html) for what this can't tell you.
 */

import { GateStreamFrame } from './gate-event-stream';

export interface GateEventRecord extends GateStreamFrame {
  id: string;
  laneId: string;
  laneName: string;
  gateSerial: string;
}

export interface GateEventWithGap extends GateEventRecord {
  /** ms since the previous event across ALL watched lanes for this location; null for the first. */
  deltaMs: number | null;
  /** HH:mm:ss.SSS in the browser's local timezone. */
  absoluteTimeLabel: string;
}

export interface StaggerGroup {
  events: GateEventWithGap[];
  startedAt: number;
  laneCount: number;
  /** True when this "sequence" spans more than one lane — the only case worth calling out. */
  isMultiLane: boolean;
}

/** Mirrors svc-sse's documented defaults (GATE_LOCATION_STAGGER_MS / GATE_STAGGER_MAX_DELAY_MS)
 * with headroom. Not read from any live config — no such endpoint exists, and this view does
 * not touch svc-sse. Purely a UI constant for the heuristic window. */
export const DEFAULT_STAGGER_GAP_MS = 4000;
export const DEFAULT_STAGGER_CAP_MS = 12000;

export function formatAbsoluteTime(epochMs: number): string {
  const date = new Date(epochMs);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

/**
 * Sorts by receive time and annotates each event with the gap since the
 * previous one (across all watched lanes, not per-lane) and an absolute-time
 * label. Safe to compute for every event regardless of grouping.
 */
export function withTimeGaps(events: GateEventRecord[]): GateEventWithGap[] {
  const sorted = [...events].sort((a, b) => a.receivedAt - b.receivedAt);
  return sorted.map((event, index) => ({
    ...event,
    deltaMs: index === 0 ? null : event.receivedAt - sorted[index - 1].receivedAt,
    absoluteTimeLabel: formatAbsoluteTime(event.receivedAt),
  }));
}

/**
 * Session-windowing over already-gapped events: a new group starts whenever
 * the gap since the last event in the current group exceeds `gapMs`, or the
 * span since the group's first event exceeds `capMs`. Approximate by design —
 * see module docblock.
 */
export function groupStaggeredSequences(
  events: GateEventWithGap[],
  {
    gapMs = DEFAULT_STAGGER_GAP_MS,
    capMs = DEFAULT_STAGGER_CAP_MS,
  }: { gapMs?: number; capMs?: number } = {}
): StaggerGroup[] {
  const groups: StaggerGroup[] = [];
  let current: GateEventWithGap[] = [];

  for (const event of events) {
    if (current.length === 0) {
      current = [event];
      continue;
    }
    const last = current[current.length - 1];
    const first = current[0];
    const gapFromLast = event.receivedAt - last.receivedAt;
    const spanFromStart = event.receivedAt - first.receivedAt;
    if (gapFromLast <= gapMs && spanFromStart <= capMs) {
      current.push(event);
    } else {
      groups.push(buildGroup(current));
      current = [event];
    }
  }
  if (current.length > 0) {
    groups.push(buildGroup(current));
  }
  return groups;
}

function buildGroup(events: GateEventWithGap[]): StaggerGroup {
  const laneCount = new Set(events.map(e => e.laneId)).size;
  return {
    events,
    startedAt: events[0].receivedAt,
    laneCount,
    isMultiLane: laneCount > 1,
  };
}
